// Super Admin: List boilerplate surveys and create/edit them.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';
import Layout from '../Layout';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const SurveysManagement = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isSuperAdmin()) {
      loadSurveys();
    }
  }, []);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, 'surveys'));
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSurveys(list);
    } catch (err) {
      console.error('Error loading surveys:', err);
      toast.error('Failed to load surveys');
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (survey) => {
    if (!window.confirm(`Delete survey "${survey.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'surveys', survey.id));
      toast.success('Survey deleted');
      loadSurveys();
    } catch (err) {
      console.error('Error deleting survey:', err);
      toast.error('Failed to delete survey');
    }
  };

  if (!isSuperAdmin()) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">You don&apos;t have permission to access this area.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/app-admin')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Admin
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Surveys</h1>
          <p className="mt-2 text-gray-600">
            Boilerplate surveys that can be subscribed to by disciplemakers. Build surveys from the question pool.
          </p>
        </div>

        <div className="mb-6 flex flex-wrap gap-4">
          <button
            onClick={() => navigate('/admin/surveys/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Survey
          </button>
          <button
            onClick={() => navigate('/admin/survey-questions')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            Manage Question Pool
          </button>
        </div>

        {loading && surveys.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            <p className="mt-4 text-gray-600">Loading surveys...</p>
          </div>
        ) : surveys.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-600">No surveys yet. Create a survey to get started.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {surveys.map((survey) => (
                <li key={survey.id} className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{survey.name}</h3>
                      {survey.description && (
                        <p className="mt-1 text-sm text-gray-600">{survey.description}</p>
                      )}
                      {survey.isBoilerplate && (
                        <span className="inline-flex items-center mt-2 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Boilerplate
                        </span>
                      )}
                    </div>
                    <div className="ml-4 flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/admin/surveys/${survey.id}/edit`)}
                        className="inline-flex items-center p-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        aria-label="Edit survey"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(survey)}
                        className="inline-flex items-center p-2 border border-gray-300 rounded-md text-red-700 bg-white hover:bg-red-50"
                        aria-label="Delete survey"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SurveysManagement;
